import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  hasCompletedTour,
  hasSeenTutorial,
  markTourCompleted,
  markTutorialSeen,
} from '../utils/tutorialSession';
import { NEXT_TOUR, TOUR_META, TOUR_ORDER } from './tourDefinitions';
import { destroyActiveTour, runTour } from './runTour';
import TourCatalogModal from '../components/tutorial/TourCatalogModal';
import KeepGoingDialog from '../components/tutorial/KeepGoingDialog';
import { clearMeetingSessionFlag } from '../utils/meetingSession';

const TourContext = createContext(null);

export function useTours() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTours must be used within TourProvider');
  }
  return ctx;
}

export function useToursOptional() {
  return useContext(TourContext);
}

export function TourProvider({ children, orgId: orgIdProp = null }) {
  const navigate = useNavigate();
  const params = useParams();
  const { user, activeOrganizationId } = useAuth();
  const orgId = orgIdProp || params.orgId || activeOrganizationId;

  const [showCatalog, setShowCatalog] = useState(false);
  const [keepGoingTourId, setKeepGoingTourId] = useState(null);
  const [tourBusy, setTourBusy] = useState(false);
  const [, bump] = useState(0);
  const busyRef = useRef(false);
  const refreshBadges = () => bump((n) => n + 1);

  const resetBusy = useCallback(() => {
    busyRef.current = false;
    setTourBusy(false);
  }, []);

  const openCatalog = useCallback(() => {
    destroyActiveTour();
    resetBusy();
    markTutorialSeen(user?.id);
    setKeepGoingTourId(null);
    setShowCatalog(true);
  }, [user?.id, resetBusy]);

  const closeCatalog = useCallback(() => {
    markTutorialSeen(user?.id);
    setShowCatalog(false);
  }, [user?.id]);

  const startTour = useCallback(
    async (tourId) => {
      if (!orgId) {
        console.warn('[tours] No organization id — cannot start tour');
        return;
      }
      // Allow restart even if a previous run left busy stuck
      destroyActiveTour();
      busyRef.current = true;
      setTourBusy(true);
      setShowCatalog(false);
      setKeepGoingTourId(null);
      markTutorialSeen(user?.id);

      // Meeting / Chair Dashboard tours expect a clean meeting surface
      if (tourId === 'meeting' || tourId === 'chair-dashboard') {
        clearMeetingSessionFlag();
      }
      if (tourId === 'observer') {
        window.dispatchEvent(
          new CustomEvent('pp-tour-dismiss-meeting-wizard', { detail: { tourSafe: true } })
        );
      }

      try {
        await runTour({
          tourId,
          orgId,
          navigate,
          onFinished: ({ completed, tourId: id }) => {
            resetBusy();
            if (completed) {
              markTourCompleted(user?.id, id);
              refreshBadges();
              setKeepGoingTourId(id);
            }
          },
        });
      } catch (err) {
        console.warn('[tours] run failed', err);
        resetBusy();
      }
    },
    [orgId, navigate, user?.id, resetBusy]
  );

  const continueNext = useCallback(() => {
    const finishedId = keepGoingTourId;
    const next = finishedId ? NEXT_TOUR[finishedId] : null;
    setKeepGoingTourId(null);
    resetBusy();
    if (next) {
      // Defer so React clears KeepGoingDialog before driver overlay mounts
      window.setTimeout(() => {
        void startTour(next);
      }, 50);
    } else {
      setShowCatalog(true);
    }
  }, [keepGoingTourId, startTour, resetBusy]);

  const dismissKeepGoing = useCallback(() => {
    setKeepGoingTourId(null);
    resetBusy();
  }, [resetBusy]);

  const value = useMemo(
    () => ({
      openCatalog,
      closeCatalog,
      startTour,
      showCatalog,
      tourBusy,
      orgId,
      hasCompleted: (id) => hasCompletedTour(user?.id, id),
      hasSeenIntro: () => hasSeenTutorial(user?.id),
      shouldAutoOpenCatalog: () => !hasSeenTutorial(user?.id),
    }),
    [openCatalog, closeCatalog, startTour, showCatalog, tourBusy, orgId, user?.id]
  );

  const nextId = keepGoingTourId ? NEXT_TOUR[keepGoingTourId] : null;
  const nextMeta = nextId ? TOUR_META[nextId] : null;
  const finishedMeta = keepGoingTourId ? TOUR_META[keepGoingTourId] : null;

  return (
    <TourContext.Provider value={value}>
      {children}
      <TourCatalogModal
        open={showCatalog}
        tourOrder={TOUR_ORDER}
        tourMeta={TOUR_META}
        hasCompleted={(id) => hasCompletedTour(user?.id, id)}
        busy={tourBusy}
        onClose={closeCatalog}
        onStart={(id) => void startTour(id)}
      />
      <KeepGoingDialog
        open={Boolean(keepGoingTourId)}
        finishedTitle={finishedMeta?.title || 'Tour'}
        nextTitle={nextMeta?.title || null}
        onKeepGoing={continueNext}
        onDone={dismissKeepGoing}
        onBrowseCatalog={() => {
          setKeepGoingTourId(null);
          resetBusy();
          setShowCatalog(true);
        }}
      />
    </TourContext.Provider>
  );
}
